<script setup lang="ts">
import type { AppDetailsSchema } from '~/types/product.model'

const props = defineProps<{
  appData: AppDetailsSchema
}>()

const isFetchingAppConnectUrl = ref(false)
const isFetchingBookDemoUrl = ref(false)

const badge = computed(() => {
  const [firstBadge] = props.appData.badges
  return firstBadge
})

const redirectAndTrackAppConnectButton = async (): Promise<void> => {
  try {
    isFetchingAppConnectUrl.value = true
    // Even though we track the number of backend requests, keep tracking to Mixpanel for usage in funnels
    trackAppConnectButtonClick()

    await getAppUrlAndRedirect(props.appData.id, 'call_to_action', 'app_details')
  } finally {
    isFetchingAppConnectUrl.value = false
  }
}

const redirectAndTrackBookDemoButton = async (): Promise<void> => {
  try {
    isFetchingBookDemoUrl.value = true
    // Even though we track the number of backend requests, keep tracking to Mixpanel for usage in funnels
    trackBookDemoButtonClick()

    await getAppUrlAndRedirect(props.appData.id, 'book_demo', 'app_details')
  } finally {
    isFetchingBookDemoUrl.value = false
  }
}

const trackAppConnectButtonClick = useTrackAppConnectButtonClick({
  appId: props.appData.id,
  appTitle: props.appData.title,
})

const trackBookDemoButtonClick = useTrackBookDemoButtonClick({
  appId: props.appData.id,
  appTitle: props.appData.title,
})

const connectButtonProps = computed(() => ({
  type: props.appData.has_book_demo_url ? 'secondary' : 'primary',
  dataTest: 'connect-app-url-redirect',
  loading: isFetchingAppConnectUrl.value,
  onClick: redirectAndTrackAppConnectButton,
} as const))

</script>

<template>
  <header
    v-if="appData"
    class="app-details-header app-panel"
  >
    <ProductLogo
      class="app-details-header__logo"
      :src="appData.logo_url || ''"
      alt=""
      :width="140"
      :height="140"
      sizes="md:60px lg:140px"
      preset="card-list-logo"
    />
    <span class="app-details-header__heading">
      <h1
        class="heading-2 mb-0"
        data-test="app-details-header-title"
      >
        {{ appData.title }}
      </h1>
      <ProductBadge
        v-if="badge"
        :badge="badge"
      />
    </span>
    <p class="app-details-header__short-description p-adaptive">
      {{ appData.short_description }}
    </p>
    <div class="app-details-header__button-container">
      <UiButton
        v-if="appData.has_book_demo_url"
        type="primary"
        size="large-adaptive"
        class="app-details-header__button"
        data-test="book-demo"
        :loading="isFetchingBookDemoUrl"
        @click="redirectAndTrackBookDemoButton"
      >
        Book demo
      </UiButton>
      <UiButton
        :type="connectButtonProps.type"
        size="large-adaptive"
        class="app-details-header__button"
        :data-test="connectButtonProps.dataTest"
        :loading="connectButtonProps.loading"
        @click="connectButtonProps.onClick"
      >
        Connect App
      </UiButton>
    </div>
  </header>
</template>

<style lang="scss" scoped>
@use '@/assets/styles/helpers' as *;

.app-details-header {
  display: grid;

  @include min-width('lg') {
    grid-template-areas:
      'logo heading CTA'
      'logo heading CTA'
      'logo description CTA'
      'logo description CTA';
    column-gap: 24px;
    grid-template-columns: 140px auto fit-content(100%);
  }

  @include max-width('lg') {
    grid-template-areas:
      'logo heading'
      'description description'
      'CTA CTA';
    gap: 24px 16px;
    grid-template-columns: 60px auto;
  }

  .app-details-header__button {
    text-wrap: nowrap;

    @include max-width('lg') {
      display: block;
      text-align: center;
    }
  }
}

.app-details-header__logo {
  border-radius: var(--app-border-radius-large);
  grid-area: logo;
  width: 140px;
  height: 140px;

  @include max-width('lg') {
    width: 60px;
    height: 60px;

    --app-border-radius: var(--app-border-radius-small);
  }
}

.app-details-header__heading {
  grid-area: heading;
  display: flex;
  flex-wrap: wrap;
  gap: var(--app-spacer-16);

  @include max-width('lg') {
    flex-direction: column;
    align-items: start;
    justify-content: space-between;
  }

  @include min-width('lg') {
    align-items: center;
    align-self: end;
  }
}

.app-details-header__short-description {
  grid-area: description;

  @include min-width('lg') {
    margin-bottom: 0;
  }

  @include max-width('lg') {
    margin: 0;
  }
}

.app-details-header__button-container {
  grid-area: CTA;
  text-align: right;
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: var(--app-spacer-12);

  @include min-width('lg') {
    flex-direction: column;
    justify-content: center;
  }
}
</style>
